import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { classNames } from 'primereact/utils';
import authService from '../../services/auth.service';
import oauthService from '../../services/oauth.service';

const LoginV2 = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    
    // Form state
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        rememberMe: false
    });
    
    // UI state
    const [loading, setLoading] = useState(false);
    const [oauthLoading, setOauthLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    
    // Clear errors when component mounts
    useEffect(() => {
        setErrors({});
        setSubmitted(false);
    }, []);
    
    // Handle input change
    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'rememberMe' ? checked : value
        }));
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };
    
    // Validate form
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        
        if (!formData.password) {
            newErrors.password = 'Password is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    // Handle traditional login
    const handleLogin = async (e) => {
        e.preventDefault();
        setSubmitted(true);
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        
        try {
            const result = await authService.login(formData.email, formData.password);
            
            if (result.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: result.message || 'Login successful',
                    life: 3000
                });
                
                // Redirect to dashboard after short delay
                setTimeout(() => {
                    navigate('/dashboard');
                }, 1000);
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Login Failed',
                    detail: result.message || 'Invalid email or password',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Login error:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'An unexpected error occurred. Please try again.',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };
    
    // Handle Google OAuth login - Simple redirect
    const handleGoogleLogin = async () => {
        setOauthLoading(true);
        
        try {
            const result = await oauthService.initiateGoogleOAuth(formData.email || null);
            
            if (result.success) {
                // Redirect to Google OAuth URL
                window.location.href = result.oauth_url;
            } else {
                // Show error if failed
                toast.current.show({
                    severity: 'error',
                    summary: 'OAuth Failed',
                    detail: result.message || 'Failed to initiate Google authentication',
                    life: 5000
                });
                setOauthLoading(false);
            }
        } catch (error) {
            console.error('OAuth error:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to connect with Google. Please try again.',
                life: 5000
            });
            setOauthLoading(false);
        }
    };
    
    const getFormErrorMessage = (name) => {
        return errors[name] && submitted && <small className="p-error">{errors[name]}</small>;
    };
    
    return (
        <div className="flex align-items-center justify-content-center min-h-screen min-w-screen overflow-hidden">
            <Toast ref={toast} />
            
            <div className="flex flex-column align-items-center justify-content-center">
                <div style={{ 
                    borderRadius: '56px', 
                    padding: '0.3rem', 
                    background: 'linear-gradient(180deg, var(--primary-color) 10%, rgba(33, 150, 243, 0) 30%)' 
                }}>
                    <div className="w-full surface-card py-8 px-5 sm:px-8" style={{ borderRadius: '53px' }}>
                        {/* Logo/Title Section */}
                        <div className="text-center mb-5">
                            <div className="text-900 text-3xl font-medium mb-3">Welcome Back!</div>
                            <span className="text-600 font-medium">Sign in to continue</span>
                        </div>
                        
                        <form onSubmit={handleLogin}>
                            {/* Email Field */}
                            <div className="field mb-4">
                                <label htmlFor="email" className="block text-900 text-xl font-medium mb-2">
                                    Email
                                </label>
                                <InputText 
                                    id="email" 
                                    name="email"
                                    type="email" 
                                    placeholder="Email address" 
                                    className={classNames('w-full md:w-30rem', { 'p-invalid': errors.email && submitted })}
                                    style={{ padding: '1rem' }}
                                    value={formData.email}
                                    onChange={handleChange}
                                    disabled={loading || oauthLoading}
                                />
                                {getFormErrorMessage('email')}
                            </div>
                            
                            {/* Password Field */}
                            <div className="field mb-3">
                                <label htmlFor="password" className="block text-900 font-medium text-xl mb-2">
                                    Password
                                </label>
                                <Password
                                    id="password"
                                    name="password"
                                    placeholder="Password"
                                    toggleMask
                                    className={classNames('w-full', { 'p-invalid': errors.password && submitted })}
                                    inputClassName="w-full p-3 md:w-30rem"
                                    value={formData.password}
                                    onChange={handleChange}
                                    disabled={loading || oauthLoading}
                                    feedback={false}
                                    inputProps={{ autoComplete: 'off' }}
                                />
                                {getFormErrorMessage('password')}
                            </div>
                            
                            {/* Remember Me & Forgot Password */}
                            <div className="flex align-items-center justify-content-between mb-5 gap-5">
                                <div className="flex align-items-center">
                                    <Checkbox 
                                        id="rememberme" 
                                        name="rememberMe"
                                        checked={formData.rememberMe} 
                                        onChange={handleChange}
                                        className="mr-2"
                                        disabled={loading || oauthLoading}
                                    />
                                    <label htmlFor="rememberme">Remember me</label>
                                </div>
                                <Link to="/auth/forgot-password" className="font-medium no-underline ml-2 text-right cursor-pointer" style={{ color: 'var(--primary-color)' }}>
                                    Forgot password?
                                </Link>
                            </div>
                            
                            {/* Login Button */}
                            <Button 
                                label="Sign In" 
                                type="submit"
                                className="w-full p-3 text-xl" 
                                loading={loading}
                                disabled={oauthLoading}
                            />
                        </form>
                        
                        {/* Divider */}
                        <Divider align="center" className="my-4">
                            <span className="text-600 font-normal text-sm">OR</span>
                        </Divider>
                        
                        {/* Google OAuth Button */}
                        <Button 
                            label="Sign in with Google"
                            icon="pi pi-google"
                            className="w-full p-3 text-xl"
                            severity="secondary"
                            outlined
                            onClick={handleGoogleLogin}
                            loading={oauthLoading}
                            disabled={loading}
                        />
                        
                        {/* Register Link */}
                        <div className="mt-5 text-center">
                            <span className="text-600 font-medium">Don't have an account? </span>
                            <Link to="/app/auth/register-v2" className="font-medium no-underline ml-2" style={{ color: 'var(--primary-color)' }}>
                                Create today!
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginV2;